import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState, } from 'react';
import { createPortal, } from 'react-dom';
import { cx, } from '../utils/ReactUtils.js';
import { getComponentPositionClass, } from './components.js';
export function Select({ value, onSelect, options, maxSelected = 1, onCommit, label = '', error, reserveErrorSpace = 0, disabled = false, className = '', position = 'normal', dropdownMaxHeight = 320, dropdownMaxWidth = '40rem', dropdownOffset = 4, }) {
    const isMulti = maxSelected > 1;
    const allowFreeText = typeof onCommit === 'function';
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(null);
    const [input, setInput] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState(null);
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const controlRef = useRef(null);
    const dropdownRef = useRef(null);
    const selectedOptions = options.filter(o => value.includes(o.value));
    const displayText = selectedOptions
        .map(o => o.displayLabel ?? o.label)
        .join(', ');
    const showDisplayText = !focused || !allowFreeText || !isMulti;
    function findNextEnabled(start, dir) {
        let i = start;
        while (i >= 0 && i < options.length) {
            const opt = options[i];
            if (opt !== undefined && !opt.disabled) {
                return i;
            }
            i += dir;
        }
        return null;
    }
    useEffect(() => {
        if (!open) {
            return;
        }
        if (value.length === 0) {
            setHighlightedIndex(null);
            return;
        }
        const firstSelected = options.findIndex(o => value.includes(o.value));
        if (firstSelected >= 0) {
            setHighlightedIndex(firstSelected);
        }
        else {
            setHighlightedIndex(null);
        }
    }, [open, options, value]);
    function commitInput() {
        if (!allowFreeText) {
            return;
        }
        const text = input.trim();
        if (text.length === 0) {
            return;
        }
        const next = onCommit?.(text);
        setInput(next ?? '');
    }
    useEffect(() => {
        function handleOutside(e) {
            const target = e.target;
            const insideRoot = !!rootRef.current?.contains(target);
            const insideDropdown = !!dropdownRef.current?.contains(target);
            if (!insideRoot && !insideDropdown) {
                setOpen(false);
                setFocused(false);
                commitInput();
            }
        }
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [input]);
    useEffect(() => {
        if (!open || disabled) {
            setDropdownStyle(null);
            return;
        }
        function resolveMaxWidthCss(availableRightPx) {
            const avail = Math.max(120, Math.floor(availableRightPx));
            if (typeof dropdownMaxWidth === 'number') {
                return `${Math.min(dropdownMaxWidth, avail)}px`;
            }
            // Clamp user CSS width with viewport-available width.
            return `min(${dropdownMaxWidth}, ${avail}px)`;
        }
        function updateDropdownPosition() {
            const el = controlRef.current;
            if (!el) {
                return;
            }
            const rect = el.getBoundingClientRect();
            const viewportH = window.innerHeight;
            const viewportW = window.innerWidth;
            const padding = 8;
            const spaceBelow = viewportH - rect.bottom - dropdownOffset - padding;
            const spaceAbove = rect.top - dropdownOffset - padding;
            const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
            const maxHeight = Math.max(80, Math.min(dropdownMaxHeight, openUp ? spaceAbove : spaceBelow));
            const left = rect.left;
            const maxWidth = resolveMaxWidthCss(viewportW - left - padding);
            const next = {
                position: 'fixed',
                zIndex: 1000,
                left,
                minWidth: rect.width,
                width: 'max-content',
                maxWidth,
                maxHeight,
                overflow: 'auto',
            };
            if (openUp) {
                next.bottom = viewportH - rect.top + dropdownOffset;
            }
            else {
                next.top = rect.bottom + dropdownOffset;
            }
            setDropdownStyle(next);
        }
        updateDropdownPosition();
        window.addEventListener('resize', updateDropdownPosition);
        // Capture scroll events from any scroll container.
        window.addEventListener('scroll', updateDropdownPosition, true);
        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [open, disabled, dropdownMaxHeight, dropdownMaxWidth, dropdownOffset]);
    function toggleValue(val) {
        if (value.includes(val)) {
            onSelect(value.filter(v => v !== val));
            return;
        }
        if (value.length < maxSelected) {
            onSelect([...value, val]);
        }
    }
    function handleOptionClick(opt) {
        if (opt.disabled && !value.includes(opt.value)) {
            return;
        }
        if (isMulti) {
            toggleValue(opt.value);
        }
        else {
            onSelect([opt.value]);
            setOpen(false);
        }
    }
    function handleKeyDown(e) {
        if (disabled) {
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setOpen(true);
                setHighlightedIndex(i => {
                    if (i === null) {
                        return findNextEnabled(0, 1);
                    }
                    return findNextEnabled(i + 1, 1);
                });
                break;
            case 'ArrowUp':
                e.preventDefault();
                setOpen(true);
                setHighlightedIndex(i => {
                    if (i === null) {
                        return findNextEnabled(options.length - 1, -1);
                    }
                    return findNextEnabled(i - 1, -1);
                });
                break;
            case 'Enter':
                e.preventDefault();
                if (open && highlightedIndex !== null) {
                    const opt = options[highlightedIndex];
                    if (opt !== undefined) {
                        handleOptionClick(opt);
                    }
                }
                else {
                    commitInput();
                }
                break;
            case 'Escape':
                setOpen(false);
                setFocused(false);
                break;
        }
    }
    function handleControlMouseDown(e) {
        e.preventDefault();
        if (disabled) {
            return;
        }
        setOpen(o => !o);
        inputRef.current?.focus();
    }
    const errorMinHeight = reserveErrorSpace > 0
        ? { minHeight: `${reserveErrorSpace * 1.25}rem` }
        : undefined;
    const positionClass = getComponentPositionClass(position);
    let borderCss = 'border-tym-component-border-default hover:border-tym-component-border-hover';
    if (error) {
        borderCss = 'border-error';
    }
    else if (focused) {
        borderCss = 'border-tym-component-border-active';
    }
    return (_jsxs("div", { ref: rootRef, className: `w-full ${positionClass} ${className}`, children: [_jsxs("div", { className: "relative", children: [_jsx("label", { className: cx('absolute z-10 px-1 pointer-events-none', 'overflow-hidden text-ellipsis whitespace-nowrap', 'bg-white component-label-color', 'transition-all duration-200 ease-out', 
                        // reserve more space on the right for the chevron when narrow
                        displayText ? 'left-1 -top-2 text-xs' : 'left-2 right-10 top-2.5'), children: label }), _jsxs("div", { ref: controlRef, role: "combobox", "aria-expanded": open, className: cx('flex items-center px-3 py-2 border rounded-md bg-white', 'transition-colors duration-200 ease-out', borderCss, disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'), onMouseDown: handleControlMouseDown, children: [_jsx("input", { ref: inputRef, type: "text", value: showDisplayText ? displayText : input, readOnly: !allowFreeText, className: cx('flex-1 min-w-0 bg-transparent outline-none truncate pr-2', disabled ? 'cursor-not-allowed' : 'cursor-pointer'), onFocus: () => {
                                    setFocused(true);
                                }, onBlur: () => {
                                    setFocused(false);
                                }, onChange: e => {
                                    if (allowFreeText) {
                                        setInput(e.target.value);
                                    }
                                }, onKeyDown: handleKeyDown, disabled: disabled }), _jsx("svg", { className: cx('h-5 w-5 shrink-0 z-20 transition-transform duration-200 ml-1', { 'rotate-180': open }), viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { d: "M5.23 7.21L10 10.94l4.77-3.73" }) })] }), open && !disabled && dropdownStyle && typeof document !== 'undefined' && createPortal(_jsx("ul", { ref: dropdownRef, role: "listbox", style: dropdownStyle, className: "rounded-md bg-white shadow-lg border border-tym-component-border-default", children: options.map((opt, idx) => {
                            const selected = value.includes(opt.value);
                            const highlighted = idx === highlightedIndex;
                            let stateClass = opt.disabled ? '' : 'hover:bg-gray-300';
                            if (highlighted) {
                                stateClass = 'bg-gray-100';
                            }
                            if (selected) {
                                stateClass = 'text-white bg-tym-green shadow-md';
                            }
                            return (_jsx("li", { role: "option", "aria-selected": selected, className: `px-3 py-2 select-none
                    ${opt.disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                    ${stateClass}`, onClick: () => {
                                    handleOptionClick(opt);
                                }, children: _jsxs("div", { className: "flex justify-between", children: [_jsx("span", { children: opt.label }), isMulti && selected && _jsx("span", { children: "\u2713" })] }) }, `${opt.value}`));
                        }) }), document.body)] }), _jsx("p", { className: "input-error", style: errorMinHeight, children: error ?? '' })] }));
}
//# sourceMappingURL=Select.js.map